import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import api, { formatRp, notifyError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/* Phase 3 — primitif bersama halaman operasional Pembatalan & Refund.
   Data SELALU server-side (filter + search + pagination via /admin/orders); tab/q/page hidup di URL. */

export const PAGE_SIZE = 20;
export const ORDER_STATUS_LABEL = {
  pending_payment: "Menunggu Bayar", cod_pending: "COD · Konfirmasi", cod_return_pending: "COD · Retur", cod_returned: "COD · Retur Ditutup", paid: "Dibayar", processing: "Diproses", shipped: "Dikirim", completed: "Selesai", cancelled: "Dibatalkan",
};
export const PAYMENT_STATUS_LABEL = {
  pending_verification: "Menunggu Verifikasi", verified: "Terverifikasi", rejected: "Bukti Ditolak", paid: "Dibayar (gateway)",
  expired: "Kedaluwarsa", cancelled: "Batal",
};
export const fmtDateTime = (t) => (t ? new Date(t).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }) : "-");

export function useServerOrderList({ filterKey, tabs, defaultTab }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabKey = tabs.some((t) => t.key === searchParams.get("tab")) ? searchParams.get("tab") : defaultTab;
  const tab = tabs.find((t) => t.key === tabKey);
  const q = searchParams.get("q") || "";
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const update = (patch) => {
    const next = { tab: tabKey, q, page: String(page), ...patch };
    setSearchParams(Object.fromEntries(Object.entries(next).filter(([k, v]) => v && !(k === "page" && v === "1"))));
  };
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { [filterKey]: tab.value, limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE };
      if (q) params.search = q;
      const r = await api.get("/admin/orders", { params });
      setItems(r.data.items || []); setTotal(r.data.total || 0);
    } catch (e) { notifyError(e); } finally { setLoading(false); }
  }, [filterKey, tab.value, q, page]);
  useEffect(() => { load(); }, [load]);

  return {
    tabKey, tab, q, page, items, total, loading, reload: load,
    setTab: (k) => update({ tab: k, page: "1" }), setQ: (v) => update({ q: v, page: "1" }), setPage: (p) => update({ page: String(p) }),
  };
}

export function OpsTabs({ tabs, value, onChange, counts = {}, testPrefix }) {
  return (
    <div role="tablist" aria-label="Filter status" className="flex gap-1 bg-white border border-[#E5E5E5] rounded-xl p-1 overflow-x-auto no-scrollbar">
      {tabs.map((t) => {
        const active = t.key === value;
        const n = counts[t.key];
        return (
          <button key={t.key} type="button" role="tab" aria-selected={active} onClick={() => onChange(t.key)}
            data-testid={`${testPrefix}-tab-${t.key}`}
            className={`px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap inline-flex items-center gap-2 transition-colors ${
              active ? "bg-[#FFEDD5] text-[#E85D04]" : "text-[#5C5C5C] hover:bg-[#FAF9F6]"}`}>
            {t.label}
            {n > 0 && <span className="min-w-[20px] h-[20px] px-1.5 rounded-full brand-bg-primary text-white text-[10px] font-bold grid place-items-center" data-testid={`${testPrefix}-tab-count-${t.key}`}>{n > 99 ? "99+" : n}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function OpsSearch({ value, onChange, testId }) {
  const [local, setLocal] = useState(value);
  useEffect(() => { setLocal(value); }, [value]);
  return (
    <form className="relative max-w-md" onSubmit={(e) => { e.preventDefault(); onChange(local.trim()); }}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" size={16} />
      <Input value={local} onChange={(e) => setLocal(e.target.value)} onBlur={() => local.trim() !== value && onChange(local.trim())}
        placeholder="Cari no pesanan / pelanggan… (Enter)" className="pl-9 rounded-xl" data-testid={testId} />
    </form>
  );
}

export function OpsPagination({ page, total, onPage, testPrefix }) {
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 text-xs text-[#5C5C5C]" data-testid={`${testPrefix}-pagination`}>
      <span data-testid={`${testPrefix}-total`}>{total} pesanan · Halaman {page} / {pages}</span>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" className="rounded-lg" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid={`${testPrefix}-prev`}>Sebelumnya</Button>
        <Button variant="outline" size="sm" className="rounded-lg" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid={`${testPrefix}-next`}>Berikutnya</Button>
      </div>
    </div>
  );
}

export function OrderCell({ o }) {
  return (
    <div className="min-w-0">
      <Link to={`/admin/pesanan/${o.id}`} className="font-mono text-xs text-[#E85D04] font-bold break-all" data-testid={`ops-order-link-${o.order_number}`}>{o.order_number}</Link>
      <div className="text-[11px] text-[#5C5C5C]">{fmtDateTime(o.created_at)}</div>
    </div>
  );
}

export function CustomerCell({ o }) {
  return (
    <div className="min-w-0">
      <div className="font-semibold truncate max-w-[220px]">{o.user_name || "-"}</div>
      <div className="text-xs text-[#5C5C5C] truncate max-w-[220px]">{o.user_email}</div>
    </div>
  );
}

export function PaymentCell({ o }) {
  return (
    <div className="text-xs">
      <div className="uppercase font-semibold">{o.payment_method || "-"}</div>
      <div className="text-[#5C5C5C]">{PAYMENT_STATUS_LABEL[o.payment_status] || o.payment_status || "Belum bayar"}</div>
      <div className="font-bold text-[#1A1A1A] mt-0.5">{formatRp(o.final_total ?? o.total)}</div>
    </div>
  );
}

export function StatusPill({ label, cls, testId }) {
  return <span className={`inline-block text-[10px] uppercase font-bold px-2 py-1 rounded whitespace-nowrap ${cls}`} data-testid={testId}>{label}</span>;
}

export function EmptyRow({ loading, text, colSpan }) {
  return <tr><td colSpan={colSpan} className="py-12 text-center text-[#5C5C5C]" data-testid="ops-empty">{loading ? "Memuat…" : text}</td></tr>;
}
